import { useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { claimsActions } from '../../../../store';
import {
  FBoxContainer,
  FCheckbox,
  FHAutocomplete,
  FHButton,
  FHInputField,
  FHTooltip,
} from '../../../base';
import { areUserRolesIn } from '../../../../helpers';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { Roles, Themes } from '../../../../constants';
import ExclamationIcon from '../../../svgs/ExclamationIcon';
import {
  getClaimDetailsMessage,
  getRelaxClaimValidationsPasswordInfoText,
  isRelaxClaimValidationsPasswordMismatch,
} from './utils';
import { ClaimSourceEnum } from '../../../../pages/constants';

import type { RootState } from '../../../../store';
import type { HTMLAttributes, ChangeEvent } from 'react';
import type { IFacility, IOption, IServiceCode } from '../../../../utils/types';

const INVOICE_BILLING_FACILITY_LABEL = 'Montera Health Texas LLC';

interface IClaimDetailsProps {
  isExperimentalClaim: boolean;
  selectedBillingFacilityId: number | null;
  generatePreview?: boolean;
  selectedClient: IOption | null;
  bcbaName?: string;
  authorizer?: string;
  totalNoOfSessions: number;
  selectedProcedureIds: (number | string)[];
  totalDurationOfSessions?: number;
  useDefaultBillingFacility: boolean;
  numberOfUnitsSelected: number;
  onGenerate: () => void;
  setClaimSourceLink: (source: ClaimSourceEnum) => void;
  setIsExperimentalClaim: (val: boolean) => void;
  setSelectedBillingFacility: (facilityId: number | null) => void;
  setUseDefaultBillingFacility: (val: boolean) => void;
  setTab: (tab: number) => void;
  selectedProcedure?: IServiceCode;
  relaxClaimValidationsChecked: boolean;
  relaxClaimValidationsPassword: string;
  relaxValidations: boolean;
  setRelaxClaimValidationsChecked: (value: boolean) => void;
  setRelaxClaimValidationsPassword: (value: string) => void;
}

const ClaimDetails = (props: IClaimDetailsProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const {
    isExperimentalClaim,
    generatePreview,
    selectedClient,
    bcbaName,
    authorizer,
    totalDurationOfSessions,
    totalNoOfSessions,
    useDefaultBillingFacility,
    numberOfUnitsSelected,
    onGenerate,
    setClaimSourceLink,
    setIsExperimentalClaim,
    setSelectedBillingFacility,
    setUseDefaultBillingFacility,
    setTab,
    selectedProcedure,
    selectedProcedureIds,
    selectedBillingFacilityId,
    relaxClaimValidationsChecked,
    relaxClaimValidationsPassword,
    relaxValidations,
    setRelaxClaimValidationsChecked,
    setRelaxClaimValidationsPassword,
  } = props;

  const { user } = useSelector((state: RootState) => state.auth);
  const { claimPreview } = useSelector((state: RootState) => state.claims);
  const { list: facilityList } = useSelector((state: RootState) => state.facilities);

  const [bfDropdownOpen, setBFDropdownOpen] = useState(false);

  const facilityOptions = useMemo(
    () =>
      facilityList.map((facility: IFacility) => ({
        id: facility.id,
        label: facility?.label,
      })),
    [facilityList],
  );

  const handleCreatePreview = () => {
    if (claimPreview) {
      setTab(1);
      return;
    }
    if (!selectedClient || !selectedProcedure || !selectedProcedureIds) return;
    const data = {
      client_id: selectedClient.id,
      service_code_id: selectedProcedure.id,
      procedure_ids: selectedProcedureIds,
      skip_prior_authorization: isExperimentalClaim,
      use_custom_billing_facility: useDefaultBillingFacility,
      billing_facility_id: selectedBillingFacilityId ?? null,
      relax_validations: relaxValidations,
    };
    dispatch(claimsActions.createClaimPreview({ data, getAccessTokenSilently }));
    setClaimSourceLink(ClaimSourceEnum.APP);
  };

  const selectedBillingFacilityLabel =
    facilityOptions.find((option: IOption) => option.id === selectedBillingFacilityId)?.label || '';

  return (
    <FBoxContainer contentStyle={{ p: 2 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: '700',
          textAlign: 'left',
          color: FORTA_BLUE.primary,
          px: 1,
          mb: 2,
        }}
        data-testid="claimDetailsId"
      >
        Claim details
      </Typography>
      {totalNoOfSessions > 0 ? (
        <>
          <Grid container>
            <Grid
              item
              xs={12}
              md={5.9}
              sx={{ backgroundColor: FORTA_BLUE.lightest, borderRadius: '16px', p: 2, mr: 2 }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 1,
                }}
              >
                <Box sx={{ display: 'flex', pb: 2 }} data-testid="claimDetailsClientDetails">
                  <Box sx={{ mr: 8 }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: FORTA_BLUE.primary,
                      }}
                    >
                      Client
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: FORTA_GRAY4,
                      }}
                      data-testid="clientNameId"
                    >
                      {selectedClient?.label}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: FORTA_BLUE.primary,
                      }}
                      data-testid="bcbaId"
                    >
                      BCBA
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: FORTA_GRAY4,
                      }}
                    >
                      {bcbaName}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_BLUE.primary,
                  }}
                >
                  Authorizer
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_GRAY4,
                  }}
                  data-testid="authorizerId"
                >
                  {authorizer}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_GRAY4,
                  }}
                >
                  Total No. of sessions
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_BLUE.primary,
                  }}
                  data-testid="totalNoOfSessionsId"
                >
                  {totalNoOfSessions}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, pb: 1, px: 1 }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_GRAY4,
                  }}
                >
                  Total No. of units
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_BLUE.primary,
                  }}
                  data-testid="totalDurationOfSessionsId"
                >
                  {numberOfUnitsSelected}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, pb: 1, px: 1 }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_GRAY4,
                  }}
                >
                  Total Duration of Sessions
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '700',
                    textAlign: 'left',
                    color: FORTA_BLUE.primary,
                  }}
                  data-testid="totalDurationOfSessionsId"
                >
                  {(totalDurationOfSessions ?? 0) / 3600}
                  {' hours'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={5.9}
              sx={{
                backgroundColor: generatePreview ? FORTA_BLUE.lightest : FORTA_BLUE.light3,
                borderRadius: '16px',
                p: 2,
              }}
            >
              {generatePreview ? (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <FCheckbox
                    data-testid="experimentalClaimCheckbox"
                    sx={{ m: 0, mb: 3 }}
                    checked={isExperimentalClaim}
                    onChange={(e) => setIsExperimentalClaim(e.target.checked)}
                  />
                  <Typography textAlign="left">
                    Create an experimental claim without prior authorization
                  </Typography>
                </Box>
              ) : isExperimentalClaim ? (
                <Typography
                  fontWeight={700}
                  color={FORTA_BLUE.light1}
                  textAlign={'left'}
                  px={1}
                  mt={2}
                >
                  An experimental claim will be created without prior authorization
                </Typography>
              ) : null}
              {generatePreview ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      my: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FCheckbox
                        data-testid="useDefaultBillingFacilityCheckbox"
                        sx={{ m: 0 }}
                        checked={useDefaultBillingFacility}
                        onChange={(e) => {
                          setUseDefaultBillingFacility(e.target.checked);
                          if (e.target.checked) {
                            const id = facilityOptions.find(
                              (option: IOption) => option.label === INVOICE_BILLING_FACILITY_LABEL,
                            )?.id;
                            setSelectedBillingFacility(id ?? null);
                          } else {
                            setSelectedBillingFacility(null);
                          }
                        }}
                      />
                      <Typography textAlign={'left'}>Use</Typography>
                      <FHTooltip
                        placement={bfDropdownOpen ? 'left' : 'bottom'}
                        arrow
                        disableHoverListener={!useDefaultBillingFacility}
                        tooltipBorderRadius="8px"
                        tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                        tooltipbackground={FORTA_WHITE}
                        title={<Typography>{selectedBillingFacilityLabel}</Typography>}
                      >
                        <FHAutocomplete
                          inputHeight={40}
                          sx={{ width: '100%', mx: 1, minWidth: '280px', boxShadow: 'none' }}
                          placeholder=""
                          options={facilityOptions}
                          disabled={!useDefaultBillingFacility}
                          disableClearable
                          onOpen={() => {
                            setBFDropdownOpen(true);
                          }}
                          onClose={() => {
                            setBFDropdownOpen(false);
                          }}
                          renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => (
                            <li {...props} key={option.id}>
                              {option.label}
                            </li>
                          )}
                          value={
                            facilityOptions.find(
                              (option: IOption) => option.id === selectedBillingFacilityId,
                            ) || ''
                          }
                          onChange={(
                            event: ChangeEvent<HTMLSelectElement>,
                            newValue: IOption | null,
                          ) => {
                            const facilityId = Number(newValue?.id);
                            setSelectedBillingFacility(Number.isFinite(facilityId) ? facilityId : null);
                          }}
                        />
                      </FHTooltip>
                    </Box>
                    <Typography textAlign={'left'}>billing entity.</Typography>
                  </Box>
                </>
              ) : selectedBillingFacilityId ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1, mt: 2 }}>
                  <Typography fontWeight={700} color={FORTA_BLUE.light1} textAlign="left">
                    {`Billing facility: `}
                  </Typography>
                  <Typography
                    textAlign="left"
                    ml={1}
                  >{`${selectedBillingFacilityLabel}`}</Typography>
                </Box>
              ) : null}
              {generatePreview ? (
                <>
                  <Box sx={{ display: 'flex', flexGrow: 1, mt: 4 }}>
                    <FCheckbox
                      data-testid="relaxClaimValidationsCheckbox"
                      sx={{ m: 0, mb: 1 }}
                      checked={relaxClaimValidationsChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRelaxClaimValidationsChecked(checked);
                        if (!checked) {
                          setRelaxClaimValidationsPassword('');
                        }
                      }}
                    />
                    <Typography textAlign="left">Relax claim validations</Typography>
                  </Box>
                  {relaxClaimValidationsChecked ? (
                    <Box sx={{ pl: 1, pr: 1, ml: 3.5, width: '100%' }}>
                      <FHInputField
                        data-testid="relaxClaimValidationsPasswordInput"
                        type="password"
                        placeholder="Password"
                        sx={{ width: '250px' }}
                        value={relaxClaimValidationsPassword}
                        onChange={(e) => setRelaxClaimValidationsPassword(e.target.value)}
                        autoComplete="off"
                        error={isRelaxClaimValidationsPasswordMismatch(
                          relaxClaimValidationsPassword,
                        )}
                        infoText={getRelaxClaimValidationsPasswordInfoText(
                          relaxClaimValidationsPassword,
                        )}
                      />
                    </Box>
                  ) : null}
                </>
              ) : relaxValidations ? (
                <Typography
                  fontWeight={700}
                  color={FORTA_BLUE.light1}
                  textAlign="left"
                  px={1}
                  mt={2}
                >
                  Claim validations will be relaxed when the claim is generated
                </Typography>
              ) : null}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            {generatePreview ? (
              <FHButton
                colorTheme={Themes.Primary}
                disabled={
                  !areUserRolesIn(user?.roles, [Roles.Biller]) ||
                  !(totalNoOfSessions && totalDurationOfSessions)
                }
                onClick={handleCreatePreview}
                sx={{
                  width: '168px',
                  '&.Mui-disabled': {},
                }}
                data-testid="createPreviewBtn"
              >
                {claimPreview ? 'Go to preview' : 'Create preview'}
              </FHButton>
            ) : (
              <FHButton
                disabled={
                  !areUserRolesIn(user?.roles, [Roles.Biller]) ||
                  !(totalNoOfSessions && totalDurationOfSessions)
                }
                data-testid="generateClaimId"
                onClick={onGenerate}
              >
                Generate Claim
              </FHButton>
            )}
          </Box>
        </>
      ) : (
        <Box
          sx={{ pt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          data-testid="claimDetailsMessageContainer"
        >
          <ExclamationIcon />
          <Typography variant="body1" sx={{ ml: 2, fontWeight: '700', color: FORTA_BLUE.light1 }}>
            {selectedClient && selectedProcedure
              ? getClaimDetailsMessage(selectedProcedure)
              : `Select client to build claims.`}
          </Typography>
        </Box>
      )}
    </FBoxContainer>
  );
};

export default ClaimDetails;
